import {
    Callout,
    DefaultEffects,
    DirectionalHint,
    FocusZone,
    ICalloutContentStyles,
    IProgressIndicatorStyles,
    ISearchBoxProps,
    ProgressIndicator,
    SearchBox,
    IFocusZone,
    FocusZoneDirection,
    FocusZoneTabbableElements,
    Link,
    KeyCodes, Label,
    useTheme,
    Icon,
    ISearchBox,
    ITheme,
} from "@fluentui/react";
import React from "react";
import { RenderIf } from "./RenderIf";
import HighlightTextView from "./HighlightTextView";
import Axios from "axios";

export interface ISuggestionItem {
    getSuggestionItem: (query?: string) => JSX.Element;
    getSearchText: () => string;
    getKey: () => string;
}

/** The committed choice of a single-select box, shown as a removable pill in place of the input. */
export interface ISelectedValue {
    key: string;
    text: string;
    iconName?: string;
}

interface IAutocompleteSearchBoxProps extends ISearchBoxProps {
    suggestions?: string[] | ISuggestionItem[];
    onSuggestionClicked: (suggestion: string | ISuggestionItem) => void;
    inProgress?: boolean;
    debounceTime?: number;
    onClear?: (ev?: any) => void;
    label?: string;
    labelActions?: React.ReactNode;
    /** Reopen the suggestions callout when the box gains focus (default closes it), so a
     * pre-populated list (e.g. a scoped browse) is reachable without typing. */
    showSuggestionsOnFocus?: boolean;
    /** Render the suggestions callout in a Layer (portal) instead of inline, so it can escape a
     * clipping container such as a Dialog. Default stays inline for existing layouts. */
    layerSuggestions?: boolean;
    /** Single-select mode, like the omnibox's committed chips: while set, the box shows this value as
     * a pill with a remove button instead of an editable input, so nothing else can be typed until
     * the selection is cleared. */
    selectedValue?: ISelectedValue;
    /** Fired when the selected pill is removed; the box then becomes editable and takes focus. */
    onClearSelection?: () => void;
}

// Mirrors a Fluent SearchBox at rest (32px, 1px neutral border) so the pill mode does not shift layout.
// Styles take the ThemeProvider theme: getTheme() here would return Fluent's default (blue) palette.
const selectedFieldStyle = (theme: ITheme): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    height: 32,
    padding: '0 8px',
    border: `1px solid ${theme.palette.neutralSecondary}`,
    borderRadius: 2,
    backgroundColor: theme.palette.white,
    boxSizing: 'border-box',
    minWidth: 0
});
const selectedPillStyle = (theme: ITheme): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    maxWidth: '100%',
    minWidth: 0,
    padding: '2px 4px 2px 10px',
    borderRadius: 12,
    fontSize: 12,
    lineHeight: '16px',
    color: theme.palette.white,
    backgroundColor: theme.palette.themePrimary
});
const selectedPillTextStyle: React.CSSProperties = {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
};
const selectedPillRemoveStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    padding: '0 4px',
    cursor: 'pointer',
    color: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0
};
const AutocompleteSearchBox = (props: IAutocompleteSearchBoxProps) => {
    const theme = useTheme();
    const textInput = React.useRef<HTMLDivElement>(null);
    const [isCalloutFocussed, setCalloutFocussed] = React.useState(false);
    const [isCallOutVisible, setIsCallOutVisible] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);
    const [query, setQuery] = React.useState("");
    const focusZoneRef = React.useRef<IFocusZone>(null);
    const [suggestions, setSuggestions] = React.useState<
        string[] | ISuggestionItem[]
    >();
    const [suggestionClicked,setSuggestionClicked]=React.useState(false);
    const searchBoxRef = React.useRef<ISearchBox>(null);
    // Set when the pill's remove button is used, so the editable box takes focus once it renders.
    const focusAfterClear = React.useRef(false);

    // A committed selection replaces whatever was typed; clearing it starts from an empty box.
    React.useEffect(() => {
        if (props.selectedValue) {
            setQuery("");
            setSuggestions(undefined);
            setIsCallOutVisible(false);
        } else if (focusAfterClear.current) {
            focusAfterClear.current = false;
            searchBoxRef.current?.focus();
        }
    }, [props.selectedValue?.key]);

    const clearSelection = () => {
        if (props.disabled) return;
        focusAfterClear.current = true;
        setSuggestionClicked(true); // keep the debounced onChange from firing a blank search
        setQuery("");
        props.onClearSelection?.();
    };

    React.useEffect(() => {
        setSuggestions(props.suggestions);
        setIsCallOutVisible(props.suggestions !== undefined);
    }, [props.suggestions]);

    React.useEffect(() => {
        setIsLoading(props.inProgress === true ? true : false);
    }, [props.inProgress]);

    const ProgressIndicatorStyle: Partial<IProgressIndicatorStyles> = {
        itemProgress: {
            paddingBottom: "4px",
        },
    };

    const getCalloutWidth = () => {
        const calloutWidth = textInput.current?.offsetWidth;
        return calloutWidth + "px!important";
    }

    // const getLeftShift = () => {
    //   let leftShift = textInput.current?.offsetLeft;
    //   return leftShift + "px!important";
    // }

    // The layered variant positions itself against the target like any Callout, so it must not carry
    // the inline variant's top/left pinning (written for doNotLayer rendering inside the page flow).
    const typeAheadCalloutStyle: Partial<ICalloutContentStyles> = props.layerSuggestions === true
        ? {
            root: {
                boxShadow: DefaultEffects.elevation4,
                borderRadius: 2,
                width: getCalloutWidth(),
                minWidth: "200px",
                overflow: "hidden",
            },
            calloutMain: {
                minHeight: "fit-content",
                maxHeight: "400px!important",
            },
        }
        : {
        root: {
            boxShadow: DefaultEffects.elevation4,
            borderRadius: 2,
            marginTop: 0,
            width: getCalloutWidth(),
            minWidth: "200px",
            overflow: "hidden",
            //maxHeight: '500px!important'
            top: "0px!important",
            left: "0px!important",
            //getLeftShift(),
            selectors: {
                "@media(max-width: 600px)": {
                    top: "0px",
                    left: "0px!important",
                    //left: getLeftShift(),
                    // bottom: "-200px!important",
                    minWidth: "200px",
                },
            },
        },
        container: {
            zIndex: 3,
            position: "relative",
        },
        calloutMain: {
            minHeight: "fit-content",
            maxHeight: "500px!important",
            height: "100%",
        },
    };
    const onFocus = (event: React.FocusEvent<HTMLInputElement>) => {
        setSuggestionClicked(false);
        setIsCallOutVisible(props.showSuggestionsOnFocus === true && suggestions !== undefined && suggestions.length > 0);
        if (props.onFocus) props.onFocus(event);
    };

    const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
        switch (ev.which) {
            case KeyCodes.down: {
                setCalloutFocussed(true);
                focusZoneRef.current?.focus();
                ev.preventDefault();
                break;
            }
            default:
                setCalloutFocussed(false);
        }
    };

    const renderProgressIndicator = () => {
        if (isLoading) {
            return <ProgressIndicator styles={ProgressIndicatorStyle} />;
        }
        return null;
    };

    const onSuggestionClicked = (suggestion: string | ISuggestionItem) => {
        const query =
            typeof suggestion === "string" ? suggestion : suggestion.getSearchText();
        setSuggestionClicked(true);
        setQuery(query);
        props.onSuggestionClicked(suggestion);
        hideSuggestions();
    };

    const hideSuggestions = () => {
        // console.log("HIDE CALLOUT");
        setIsCallOutVisible(false);
    };

    const onSuggestionKeyDown = (
        event: React.KeyboardEvent<any>,
        suggestion: ISuggestionItem
    ) => {
        console.log(event);
        if (event.which === KeyCodes.enter) onSuggestionClicked(suggestion);
    };
    const renderSuggestions = () => {
        const views: JSX.Element[] = [];
        if (!suggestions) return <></>;
        suggestions.forEach((suggestion: string | ISuggestionItem, i: number) => {
            if (typeof suggestion === "string") {
                views.push(getDefaultListItem(suggestion, i));
            } else {
                views.push(
                    <Link
                        style={{ margin: "2px", width: "95%" }}
                        key={i}
                        onKeyPress={(e) => onSuggestionKeyDown(e, suggestion)}
                        onClick={(e) => onSuggestionClicked(suggestion)}
                        className="oneSuggestion"
                        role="listitem"
                    >
                        {suggestion.getSuggestionItem(query)}
                    </Link>
                );
            }
        });

        return views;
    };

    const defaultSuggestionItem: React.CSSProperties = {
        width: "100%",
        float: "left",
        padding: "5px",
    };

    const getDefaultListItem = (suggestion: string, key: any) => {
        return (
            <div className="oneSuggestion" role="listitem" key={key}>
                <Link
                    onClick={(e) => onSuggestionClicked(suggestion)}
                    style={defaultSuggestionItem}
                >
                    <HighlightTextView text={suggestion} filter={query}></HighlightTextView>
                </Link>
            </div>
        );
    };

    const onCallOutDismiss = () => {
        setIsCallOutVisible(false);
    };

    const searchContainer: React.CSSProperties = {
        margin: "auto",
        width: "100%",
    };

    const onChange = (
        event?: React.ChangeEvent<HTMLInputElement> | undefined,
        newValue?: string | undefined
    ) => {
        setSuggestionClicked(false);
        setQuery(newValue || "");
    };

    React.useEffect(() => {
        if (props.onChange) {
            const { cancel, token } = Axios.CancelToken.source();
            const timeOutId = setTimeout(async () => {
                if (props.onChange &&!suggestionClicked) props.onChange(undefined, query)
            }, props.debounceTime || 0);
            return () => {
                cancel("No longer latest query");
                clearTimeout(timeOutId);
            };
        }
    }, [query]);


    return (
        <div style={searchContainer}>
            {props.label &&             
            <div style={{display: "inline-flex"}}>
                <Label required={props.required}>{props.label}</Label>
                {props.labelActions}
            </div>}
            <div ref={textInput} className={props.className}>
                {props.selectedValue
                    ? <div style={{...selectedFieldStyle(theme), opacity: props.disabled ? 0.6 : 1}} role="group" aria-label={props.placeholder}>
                        <span style={selectedPillStyle(theme)} title={props.selectedValue.text}>
                            {props.selectedValue.iconName && <Icon iconName={props.selectedValue.iconName} styles={{root: {fontSize: 12, flexShrink: 0}}} />}
                            <span style={selectedPillTextStyle}>{props.selectedValue.text}</span>
                            <button
                                type="button"
                                aria-label={`Remove ${props.selectedValue.text}`}
                                disabled={props.disabled}
                                onClick={clearSelection}
                                style={{...selectedPillRemoveStyle, cursor: props.disabled ? 'default' : 'pointer'}}
                            >
                                <Icon iconName="Cancel" styles={{root: {fontSize: 10}}} />
                            </button>
                        </span>
                    </div>
                    : <SearchBox
                        {...props}
                        componentRef={searchBoxRef}
                        autoComplete="off"
                        onChange={onChange}
                        onFocus={onFocus}
                        onKeyDown={onKeyDown}
                        value={query}
                        onClear={props.onClear}
                        required={props.required}
                    />}
            </div>
            <RenderIf
                condition={!props.selectedValue && (isLoading || (suggestions !== undefined && isCallOutVisible))}
            >
                <Callout
                    styles={typeAheadCalloutStyle}
                    isBeakVisible={false}
                    target={textInput.current}
                    onDismiss={onCallOutDismiss}
                    directionalHint={DirectionalHint.bottomLeftEdge}
                    directionalHintForRTL={DirectionalHint.bottomRightEdge}
                    setInitialFocus={isCalloutFocussed}
                    doNotLayer={props.layerSuggestions !== true}
                >
                    {renderProgressIndicator()}
                    <FocusZone
                        direction={FocusZoneDirection.bidirectional}
                        handleTabKey={FocusZoneTabbableElements.all}
                        id="focusZoneSuggestions"
                        componentRef={focusZoneRef}
                    >
                        {renderSuggestions()}
                    </FocusZone>
                </Callout>
            </RenderIf>
        </div>
    );
};

export default AutocompleteSearchBox;